import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AlertCircle, X, IndianRupee, CreditCard, Info, ChevronLeft } from 'lucide-react';
import { MonthYearPicker } from '@/components/MonthYearPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { StatusBadge, type Status } from '@/components/StatusBadge';
import { getApiErrorMessage } from '@/api/errorHandler';
import { PaymentAPIRequestMapper, PaymentAPIResponseMapper } from '@/api/mapper/payment';
import { createPayment } from '@/api/service/payment';
import { useInvalidateUserPayments, useUserPaymentsQuery } from '@/api/query/payment';
import { usePaymentContext } from '@/contexts/PaymentContext';
import type { PaymentType } from '@/api/type/payment';
import type { UpsertPaymentProps } from '@/types';
import {
  MONTHLY_FEE,
  PLAN_MONTHS,
  formatCurrency,
  getCurrentMonthLabel,
  getCurrentMonthYear,
  formatMonthYearDisplay,
  formatMonthYearPayload,
  calculateDuePeriod,
  addMonthsToMonthYear,
  compareMonthYear,
  type MonthYear,
} from '@/lib/payments';
import { dateToIso, todayDateDisplay } from '@/lib/dates';
import {
  isAddPaymentDisabled,
  getMembershipStatus,
  SUCCESS_NOTICE_CLASS,
  SUCCESS_NOTICE_ACCENT_CLASS,
} from '@/lib/utils';

const DRAWER_CLASS =
  'h-full !w-full !min-w-0 !max-w-full overflow-hidden flex flex-col sm:!w-[45vw] sm:!min-w-[650px] sm:!max-w-[45vw]';

const FIELD_CLASS =
  'h-10 box-border border-gray-200 bg-white transition-colors focus-visible:border-[#4369a0] focus-visible:ring-1 focus-visible:ring-[#4369a0]/25';

const LABEL_CLASS = 'flex min-h-5 items-center text-gray-700';

const formId = 'add-payment-form';

type PaymentFormValues = {
  amount: string;
  paymentType: PaymentType;
  dueStart: MonthYear;
  dueEnd: MonthYear;
};

function FieldError({ show, message }: { show: boolean; message?: string }) {
  if (!show || !message) return null;
  return <p className="!mt-0 block text-[13px] leading-4 text-[crimson]">{message}</p>;
}

export function UpsertPayment({ member, open, onOpenChange, onSuccess, onBack }: UpsertPaymentProps) {
  const { addPayment, getManagementByMemberId, refreshManagement } = usePaymentContext();
  const invalidateUserPayments = useInvalidateUserPayments();

  useUserPaymentsQuery(
    member?.id,
    open && Boolean(member?.id)
  );

  const managementItem = member ? getManagementByMemberId(member.id) : undefined;
  const monthsPaid = managementItem?.paid_months ?? 0;
  const totalMonths = managementItem?.total_months ?? PLAN_MONTHS;
  const paidThisMonth = managementItem?.this_month?.toLowerCase() === 'paid';

  const addPaymentDisabled = member ? isAddPaymentDisabled(member, monthsPaid) : false;
  const noDues = member ? getMembershipStatus(member, monthsPaid) === 'completed' : false;

  const defaultMonthYear = getCurrentMonthYear();

  const formik = useFormik<PaymentFormValues>({
    enableReinitialize: true,
    initialValues: {
      amount: String(MONTHLY_FEE),
      paymentType: 'Cash',
      dueStart: defaultMonthYear,
      dueEnd: defaultMonthYear,
    },
    validationSchema: yup.object({
      amount: yup
        .string()
        .trim()
        .required('Please enter amount')
        .test('amount-valid', 'Please enter a valid amount', (value) => {
          const amount = Number(value);
          return Number.isFinite(amount) && amount > 0;
        }),
      paymentType: yup.mixed<PaymentType>().oneOf(['Cash', 'Online']).required('Please select payment type'),
    }),
    validate: (values) => {
      const errors: Partial<Record<string, string>> = {};
      if (compareMonthYear(values.dueEnd, values.dueStart) < 0) {
        errors.dueEnd = 'End month cannot be before start month';
      }
      return errors;
    },
    onSubmit: async (values, { setStatus, resetForm }) => {
      if (!member || addPaymentDisabled) return;
      setStatus(undefined);

      const duePeriod = calculateDuePeriod(values.dueStart, values.dueEnd);
      const dueStart = formatMonthYearPayload(values.dueStart);
      const dueEnd = formatMonthYearPayload(values.dueEnd);
      const paymentDateIso = dateToIso(todayDateDisplay());

      try {
        const created = await createPayment(
          PaymentAPIRequestMapper({
            memberId: member.id,
            amount: Number(values.amount),
            paymentType: values.paymentType,
            paymentDate: paymentDateIso,
            duePeriod,
            dueStart,
            dueEnd,
          })
        );
        addPayment(PaymentAPIResponseMapper(created));
        void refreshManagement();
        void invalidateUserPayments(member.id);
        resetForm();
        onSuccess?.();
        if (onBack) {
          onBack();
        } else {
          onOpenChange(false);
        }
      } catch (error) {
        console.error(error);
        setStatus(getApiErrorMessage(error));
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    const cur = getCurrentMonthYear();
    formik.resetForm({
      values: {
        amount: String(MONTHLY_FEE),
        paymentType: 'Cash',
        dueStart: cur,
        dueEnd: cur,
      },
    });
  }, [open, member?.id]);

  const showFieldError = (name: keyof PaymentFormValues) =>
    Boolean((formik.touched[name] || formik.submitCount > 0) && formik.errors[name]);

  const duePeriod = calculateDuePeriod(formik.values.dueStart, formik.values.dueEnd);
  const dueNumber = Math.min(totalMonths, monthsPaid + 1);
  const dueOrdinal = (() => {
    const mod100 = dueNumber % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${dueNumber}th`;
    switch (dueNumber % 10) {
      case 1:
        return `${dueNumber}st`;
      case 2:
        return `${dueNumber}nd`;
      case 3:
        return `${dueNumber}rd`;
      default:
        return `${dueNumber}th`;
    }
  })();

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    onOpenChange(false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
          return;
        }
        if (formik.isSubmitting) return;
        formik.resetForm();
        onOpenChange(false);
      }}
      direction="right"
    >
      <DrawerContent className={DRAWER_CLASS}>
        <DrawerHeader className="shrink-0 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-2">
              {onBack && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        formik.resetForm();
                        onBack();
                      }}
                      disabled={formik.isSubmitting}
                      className="h-7 w-7 shrink-0 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      <span className="sr-only">Back</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Back to history</TooltipContent>
                </Tooltip>
              )}
              <div className="min-w-0">
                <DrawerTitle className="text-base font-semibold text-gray-900">
                  Add Payment
                </DrawerTitle>
                {member && (
                  <p className="mt-1 truncate text-sm text-gray-500">
                    <span className="tabular-nums text-gray-400">#{member.id}</span>
                    <span className="mx-1.5 text-gray-300">·</span>
                    <span className="text-gray-700">{member.name}</span>
                    <span className="mx-1.5 text-gray-300">·</span>
                    <span className="tabular-nums">
                      {monthsPaid}/{PLAN_MONTHS} months
                    </span>
                  </p>
                )}
              </div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
                  >
                    <X className="h-3.5 w-3.5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </DrawerClose>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          </div>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide bg-gray-50/80 px-6 py-5">
          {member && (
            <form id={formId} noValidate onSubmit={formik.handleSubmit} className="space-y-5">
              {addPaymentDisabled && noDues && (
                <div className={SUCCESS_NOTICE_CLASS}>
                  All <span className={SUCCESS_NOTICE_ACCENT_CLASS}>100 months</span> are paid.{' '}
                  <span className={SUCCESS_NOTICE_ACCENT_CLASS}>No dues</span> remaining — payments
                  cannot be added.
                </div>
              )}

              {addPaymentDisabled && !noDues && (
                <div className="rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-700">
                  This member account is closed. Payments cannot be added.
                </div>
              )}

              {paidThisMonth && !addPaymentDisabled && (
                <div className="rounded-lg border border-[#4369a0]/20 bg-[#4369a0]/5 px-4 py-3 text-sm text-[#4369a0]">
                  This member has already paid for {getCurrentMonthLabel()}. You can add
                  payments for other months.
                </div>
              )}

              <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <IndianRupee className="h-4 w-4 text-[#4369a0]" />
                    Payment Details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the due period (months) and payment type.
                  </p>
                </div>
                <div className="space-y-4 p-5">
                  <div className="space-y-2">
                    <div className="flex min-h-5 items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`${formId}-due-period`} className={LABEL_CLASS}>
                          Due Period *
                        </Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center text-gray-400 hover:text-gray-600"
                            >
                              <Info className="h-4 w-4" />
                              <span className="sr-only">Due period info</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            Select the starting and ending month for this payment.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      {/* <span className="text-xs font-semibold text-[#4369a0]">
                        {duePeriod} {duePeriod === 1 ? 'month' : 'months'}
                      </span> */}
                    </div>
                    <MonthYearPicker
                      id={`${formId}-due-period`}
                      startMonth={formik.values.dueStart}
                      endMonth={formik.values.dueEnd}
                      onChange={(start, end) => {
                        formik.setFieldValue('dueStart', start);
                        formik.setFieldValue('dueEnd', end);
                        const months = calculateDuePeriod(start, end);
                        formik.setFieldValue('amount', String(months * MONTHLY_FEE));
                      }}
                    />
                    <FieldError
                      show={Boolean(formik.errors.dueEnd)}
                      message={formik.errors.dueEnd as string}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex min-h-5 items-center justify-between">
                      <Label htmlFor={`${formId}-amount`} className={LABEL_CLASS}>
                        Amount (₹) *
                      </Label>
                      <span className="text-xs text-gray-400">
                        ₹{MONTHLY_FEE} / month
                      </span>
                    </div>
                    <Input
                      id={`${formId}-amount`}
                      name="amount"
                      inputMode="decimal"
                      value={formik.values.amount}
                      onChange={(e) => {
                        const val = e.target.value;
                        formik.setFieldValue('amount', val);
                        const num = Number(val);
                        if (Number.isFinite(num) && num > 0) {
                          const months = Math.floor(num / MONTHLY_FEE);
                          if (months >= 1 && months <= 100) {
                            const newEnd = addMonthsToMonthYear(formik.values.dueStart, months - 1);
                            formik.setFieldValue('dueEnd', newEnd);
                          }
                        }
                      }}
                      onBlur={formik.handleBlur}
                      placeholder={String(MONTHLY_FEE)}
                      className={FIELD_CLASS}
                    />
                    <FieldError show={showFieldError('amount')} message={formik.errors.amount} />
                  </div>

                  <div className="space-y-3">
                    <Label className={LABEL_CLASS}>Payment Type *</Label>
                    <RadioGroup
                      value={formik.values.paymentType}
                      onValueChange={(value: string) => {
                        formik.setFieldValue('paymentType', value);
                        formik.setFieldTouched('paymentType', true, false);
                      }}
                      className="grid grid-cols-2 gap-3"
                    >
                      {(['Cash', 'Online'] as const).map((type) => (
                        <label
                          key={type}
                          htmlFor={`${formId}-${type}`}
                          className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${formik.values.paymentType === type
                              ? 'border-[#4369a0]/40 bg-[#4369a0]/5 ring-1 ring-[#4369a0]/20'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                          <RadioGroupItem
                            value={type}
                            id={`${formId}-${type}`}
                            className="mt-0.5"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{type}</p>
                            <p className="text-xs text-gray-500">
                              {type === 'Cash' ? 'Collected in person' : 'UPI / bank transfer'}
                            </p>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                    <FieldError
                      show={showFieldError('paymentType')}
                      message={formik.errors.paymentType}
                    />
                  </div>
                </div>
              </section>

              <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <CreditCard className="h-4 w-4 text-[#4369a0]" />
                    Summary
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Review this payment before saving.
                  </p>
                </div>
                <div className="px-5 py-2">
                  <dl className="divide-y divide-gray-100">
                    <div className="flex items-center justify-between gap-6 py-3.5">
                      <dt className="shrink-0 text-sm text-gray-500">Member</dt>
                      <dd className="min-w-0 text-right text-sm text-gray-900">
                        <span className="block truncate">
                          <span className="font-medium text-gray-400 tabular-nums">
                            #{member.id}
                          </span>
                          <span className="mx-1.5 text-gray-300">·</span>
                          <span className="font-medium text-gray-900">{member.name}</span>
                        </span>
                        <span className="mt-0.5 block text-xs font-normal text-gray-500">
                          +91 {member.phone}
                        </span>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-6 py-3.5">
                      <dt className="shrink-0 text-sm text-gray-500">Due period</dt>
                      <dd className="min-w-0 text-right text-sm text-gray-900">
                        <span className="block font-medium">
                          {duePeriod} {duePeriod === 1 ? 'month' : 'months'} ({dueOrdinal} due)
                        </span>
                        <span className="mt-0.5 block text-xs font-normal text-gray-500">
                          {formatMonthYearDisplay(formik.values.dueStart)}
                          {duePeriod > 1 && ` – ${formatMonthYearDisplay(formik.values.dueEnd)}`}
                        </span>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-6 py-3.5">
                      <dt className="shrink-0 text-sm text-gray-500">Payment date</dt>
                      <dd className="min-w-0 text-right text-sm text-gray-900">
                        <span className="font-medium tabular-nums text-gray-900">
                          {todayDateDisplay()}
                        </span>
                        <span className="mt-0.5 block text-[11px] font-normal text-gray-400">
                          (Today)
                        </span>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-6 py-3.5">
                      <dt className="shrink-0 text-sm text-gray-500">Amount</dt>
                      <dd className="text-right text-sm font-semibold tabular-nums text-gray-900">
                        {formatCurrency(Number(formik.values.amount) || 0)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-6 py-3.5">
                      <dt className="shrink-0 text-sm text-gray-500">Payment type</dt>
                      <dd className="text-right">
                        <StatusBadge
                          status={formik.values.paymentType.toLowerCase() as Status}
                        />
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>
            </form>
          )}
        </div>

        <DrawerFooter className="shrink-0 flex-row justify-end gap-2 border-t border-gray-200 bg-white px-6 py-4">
          {formik.status && (
            <div className="mr-auto flex min-w-0 flex-1 items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formik.status}</span>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="border-gray-200"
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={!member || addPaymentDisabled || formik.isSubmitting}
            className="min-w-36 bg-[#4369a0] text-white hover:bg-[#4369a0]/90 disabled:opacity-60"
          >
            {formik.isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Saving...
              </span>
            ) : (
              'Add Payment'
            )}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
